import React, { useEffect, useMemo } from "react";
import { Redirect } from "react-router-dom";

import {
  LOAD_DEVICE_EVENTS_V3_FETCH,
  unloadDeviceEventsV3Action,
} from "cortex/ui/src/fleet/redux-store/ui-state/device-events-v3/actions";
import { deviceEventsV3Selector } from "cortex/ui/src/fleet/redux-store/ui-state/device-events-v3/selectors";
import EventsTableContainer from "cortex/ui/src/fleet/view/event/component/EventsTableContainer";
import type { Event } from "cortex/ui/src/fleet/view/event/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import {
  applicationsSelector,
  applicationsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import {
  deviceStoresLoadedSelector,
  devicesSelector,
} from "cortex/ui/src/redux-store/devices";
import { defaultArray } from "cortex/ui/src/redux-store/selectors";
import { RootState } from "cortex/ui/src/redux-store/store";
import { NavLoader } from "cortex/ui/src/ui/loader";
import { Panel } from "cortex/ui/src/view/layout";

import type { Device } from "./model";

interface DeviceEventsProps {
  device?: Device | null;
  storesLoaded: boolean;
}

interface DeviceEventsState {
  events?: ReadonlyArray<Event>;
}

export function DeviceEvents({
  device,
  storesLoaded: parentStoresLoaded,
}: DeviceEventsProps) {
  const dispatch = useAppDispatch();
  const applications = useAppSelector(applicationsSelector);
  const applicationsStoresLoaded = useAppSelector(
    applicationsStoresLoadedSelector,
  );
  const devices = useAppSelector(devicesSelector);
  const devicesStoresLoaded = useAppSelector(deviceStoresLoadedSelector);
  const eventsV3 = useAppSelector(
    (state: RootState) =>
      ((deviceEventsV3Selector(state) as DeviceEventsState).events ??
        defaultArray) as ReadonlyArray<Event>,
  );
  const storesLoaded = useMemo(
    () => parentStoresLoaded && applicationsStoresLoaded && devicesStoresLoaded,
    [parentStoresLoaded, applicationsStoresLoaded, devicesStoresLoaded],
  );

  useEffect(() => {
    return () => {
      dispatch(unloadDeviceEventsV3Action);
    };
  }, [dispatch]);

  if (!storesLoaded) {
    return <NavLoader.Proxy />;
  }

  if (!device) {
    return <Redirect to="/404" />;
  }

  return (
    <Panel p={1}>
      <EventsTableContainer
        applications={applications}
        device={device}
        devices={devices}
        events={[...eventsV3]}
        fetchMethod={(serviceArgs: {
          deviceId?: string;
          end?: string;
          filters: Record<string, string[]>;
          start?: string;
        }) => {
          const { deviceId, end, filters, start } = serviceArgs;

          return createPromiseApiAction(dispatch, LOAD_DEVICE_EVENTS_V3_FETCH, [
            deviceId,
            start,
            end,
            filters,
          ]) as Promise<{
            data: {
              data: Event[];
            };
          }>;
        }}
      />
    </Panel>
  );
}

export default DeviceEvents;
